import { MessagePrimitive, MessageProvider } from '@assistant-ui/react';
import { MarkdownTextPrimitive } from '@assistant-ui/react-markdown';
import '@assistant-ui/react-markdown/styles/dot.css';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { ChatRole } from 'components/Chat/chatConstants';
import {
    formatDateLabel,
    formatTimeLabel,
    getDateKey,
    getMessageText,
    preserveMarkdownLineBreaks,
} from 'components/Chat/chatHelpers';
import { useSnackbar } from 'contexts/snackbar';
import { getSubmissionCreatedDraftNumber } from 'utils/chat';

import { IconButton, Stack, styled, Tag, Text } from 'shell/components';

const groupMessagesByDate = (messages = []) => {
    return messages.reduce((groups, message) => {
        const createdAt = message.createdAt || new Date();
        const dateKey = getDateKey(createdAt);
        const currentGroup = groups[groups.length - 1];
        const timelineItem = {
            id: message.id,
            kind: 'message',
            timestamp: createdAt,
            message,
        };

        if (currentGroup?.dateKey === dateKey) {
            currentGroup.items.push(timelineItem);
            return groups;
        }

        return [...groups, { dateKey, date: createdAt, items: [timelineItem] }];
    }, []);
};

const buildTimeline = ({ messages }) => {
    const seenSubmissionCreatedDraftNumbers = new Set();
    const dedupedMessages = messages.filter((message) => {
        const rawMessage = {
            role: message.role,
            message_text: getMessageText(message.content || []),
            system_type: message.metadata?.custom?.system_type,
            system_data: message.metadata?.custom?.system_data,
        };
        const draftNumber = getSubmissionCreatedDraftNumber(rawMessage);

        if (draftNumber === null) return true;
        if (seenSubmissionCreatedDraftNumbers.has(draftNumber)) return false;

        seenSubmissionCreatedDraftNumbers.add(draftNumber);
        return true;
    });

    const sortedMessages = [...dedupedMessages].sort((left, right) => {
        const leftTime = new Date(left.createdAt || 0).getTime();
        const rightTime = new Date(right.createdAt || 0).getTime();

        return leftTime - rightTime;
    });

    return groupMessagesByDate(sortedMessages);
};

const getSystemMessagePresentation = ({ message, t }) => {
    const systemType = message.metadata?.custom?.system_type;
    const systemData = message.metadata?.custom?.system_data;
    const messageText = getMessageText(message.content || []);

    if (systemType === 'submission_created') {
        return {
            draftNumber: systemData?.draftNumber,
            showTimestamp: true,
            text: t('chat.submission-created', 'has been submitted at'),
        };
    }

    if (!messageText.trim()) return null;

    return {
        draftNumber: null,
        showTimestamp: true,
        text: messageText,
    };
};

const ChatMessages = ({ bottomInset = 0, hasLoadingPlaceholder, isRunning, messages }) => {
    const { t } = useTranslation();
    const { message } = useSnackbar();
    const contentRef = useRef(null);

    const copyAssistantMessage = async ({ fallbackText, messageId }) => {
        try {
            const messageElement = [...(contentRef.current?.querySelectorAll('[data-message-content-id]') || [])].find(
                (element) => element.dataset.messageContentId === messageId
            );

            if (messageElement && navigator.clipboard.write && typeof ClipboardItem !== 'undefined') {
                await navigator.clipboard.write([
                    new ClipboardItem({
                        'text/html': new Blob([messageElement.innerHTML], { type: 'text/html' }),
                        'text/plain': new Blob([messageElement.innerText], { type: 'text/plain' }),
                    }),
                ]);
                message.success(t('common:message.copied-to-clipboard', 'Copied to clipboard.'));
                return;
            }

            await navigator.clipboard.writeText(fallbackText);
            message.success(t('common:message.copied-to-clipboard', 'Copied to clipboard.'));
        } catch (error) {
            console.error(error);
        }
    };

    const timeline = buildTimeline({ messages });
    const lastMessageId = messages[messages.length - 1]?.id;

    return (
        <MessagesStack ref={contentRef} spacing={2} bottomInset={bottomInset}>
            {timeline.map((group) => (
                <Stack key={group.dateKey} spacing={1}>
                    <DateDivider>
                        <Text color={'light'} variant={'p3'}>
                            {formatDateLabel(group.date)}
                        </Text>
                    </DateDivider>
                    {group.items.map((item) => {
                        const { message } = item;
                        const createdAt = message.createdAt || new Date();

                        if (message.role === ChatRole.SYSTEM) {
                            const systemMessagePresentation = getSystemMessagePresentation({ message, t });

                            if (!systemMessagePresentation) return null;

                            return (
                                <SystemMessageRow key={message.id} alignItems={'center'} direction={'row'} spacing={1}>
                                    {typeof systemMessagePresentation.draftNumber === 'number' ? (
                                        <>
                                            <ChatDraftTag
                                                type={'draft'}
                                                value={systemMessagePresentation.draftNumber}
                                            />
                                            <Text color={'light'} variant={'p3'}>
                                                {systemMessagePresentation.text}
                                            </Text>
                                        </>
                                    ) : (
                                        <Text color={'light'} variant={'p3'}>
                                            {systemMessagePresentation.text}
                                        </Text>
                                    )}
                                    {systemMessagePresentation.showTimestamp ? (
                                        <Text color={'light'} variant={'p3'}>
                                            {formatTimeLabel(createdAt)}
                                        </Text>
                                    ) : null}
                                </SystemMessageRow>
                            );
                        }

                        const messageText = getMessageText(message.content || []);
                        const hasMessageText = Boolean(messageText.trim());
                        const isStreamingAssistantMessage =
                            isRunning && message.role === ChatRole.ASSISTANT && message.id === lastMessageId;
                        const shouldShowMessageMeta =
                            message.role === ChatRole.ASSISTANT ? hasMessageText && !isStreamingAssistantMessage : true;
                        const messageIndex = messages.findIndex((currentMessage) => currentMessage.id === message.id);

                        return (
                            <MessageRow key={message.id} role={message.role} spacing={1}>
                                <MessageBubble role={message.role}>
                                    {message.role === ChatRole.ASSISTANT ? (
                                        <AssistantMessageContent data-message-content-id={message.id}>
                                            {isStreamingAssistantMessage ? (
                                                <Text color={'inherit'} sx={{ whiteSpace: 'pre-wrap' }}>
                                                    {messageText}
                                                </Text>
                                            ) : (
                                                <AssistantMessageMarkdown
                                                    fallbackText={messageText}
                                                    message={message}
                                                    messageIndex={messageIndex}
                                                />
                                            )}
                                        </AssistantMessageContent>
                                    ) : (
                                        <Text color={'inherit'} sx={{ whiteSpace: 'pre-wrap' }}>
                                            {messageText}
                                        </Text>
                                    )}
                                </MessageBubble>
                                {shouldShowMessageMeta ? (
                                    <MessageMetaRow role={message.role} direction={'row'} spacing={0.5}>
                                        <MessageTimestamp color={'light'} role={message.role} variant={'p3'}>
                                            {formatTimeLabel(createdAt)}
                                        </MessageTimestamp>
                                        {message.role === ChatRole.ASSISTANT ? (
                                            <CopyButton
                                                aria-label={'Copy assistant response'}
                                                kind={'copy'}
                                                size={'small'}
                                                variant={'square'}
                                                onClick={() =>
                                                    copyAssistantMessage({
                                                        fallbackText: messageText,
                                                        messageId: message.id,
                                                    })
                                                }
                                            />
                                        ) : null}
                                    </MessageMetaRow>
                                ) : null}
                            </MessageRow>
                        );
                    })}
                </Stack>
            ))}
            {hasLoadingPlaceholder ? (
                <LoadingMessageRow spacing={1}>
                    <LoadingMessageText variant={'p3'}>Thinking...</LoadingMessageText>
                </LoadingMessageRow>
            ) : null}
        </MessagesStack>
    );
};

const AssistantMessageMarkdown = ({ fallbackText, message, messageIndex }) => {
    if (messageIndex < 0) {
        return <Text color={'inherit'} sx={{ whiteSpace: 'pre-wrap' }}>{fallbackText}</Text>;
    }

    return (
        <MessageProvider index={messageIndex} isLast={false} message={message}>
            <MessagePrimitive.Parts>
                {({ part }) => {
                    if (part.type !== 'text') return null;

                    return <StyledMarkdownTextPrimitive preprocess={preserveMarkdownLineBreaks} smooth={false} />;
                }}
            </MessagePrimitive.Parts>
        </MessageProvider>
    );
};

const StyledMarkdownTextPrimitive = styled(MarkdownTextPrimitive)(({ theme }) => {
    return {
        lineHeight: '20px',
        '& p': {
            margin: 0,
        },
        '& p + p': {
            marginTop: theme.spacing(2),
        },
        '& ul, & ol': {
            margin: 0,
            paddingLeft: theme.spacing(4),
        },
        '& ul + p, & ol + p, & p + ul, & p + ol': {
            marginTop: theme.spacing(2),
        },
        '& li + li': {
            marginTop: theme.spacing(0.5),
        },
        '& li > p': {
            margin: 0,
        },
        '& h1, & h2, & h3, & h4, & h5, & h6': {
            margin: 0,
            fontSize: 'inherit',
            fontWeight: theme.typography.fontWeightMedium,
            lineHeight: 'inherit',
        },
        '& h1 + p, & h2 + p, & h3 + p, & h4 + p, & h5 + p, & h6 + p': {
            marginTop: theme.spacing(1),
        },
        '& pre, & blockquote': {
            margin: 0,
        },
    };
});

const MessagesStack = styled(Stack, {
    shouldForwardProp: (prop) => prop !== 'bottomInset',
})(({ theme, bottomInset }) => {
    const bottomPadding = bottomInset + 16;

    return {
        minHeight: 0,
        transition: 'padding-bottom 350ms ease',

        '& > *:first-child': {
            paddingTop: theme.spacing(4),
        },
        '& > *:last-child': {
            paddingBottom: `${bottomPadding}px`,
            transition: 'padding-bottom 350ms ease',
        },
    };
});

const DateDivider = styled(Stack)(() => {
    return {
        alignItems: 'center',
        textAlign: 'center',
    };
});

const SystemMessageRow = styled(Stack)(({ theme }) => {
    return {
        paddingBottom: theme.spacing(1),
    };
});

const ChatDraftTag = styled(Tag)(({ theme }) => {
    return {
        background: theme.palette.ensightful.primary[50],
        color: theme.palette.ensightful.neutral[800],
        height: 'auto',
    };
});

const MessageRow = styled(Stack, {
    shouldForwardProp: (prop) => prop !== 'role',
})(({ role }) => {
    return {
        alignItems: role === ChatRole.USER ? 'flex-end' : 'flex-start',
    };
});

const MessageBubble = styled(Stack, {
    shouldForwardProp: (prop) => prop !== 'role',
})(({ theme, role }) => {
    return {
        maxWidth: '85%',
        lineHeight: '20px',
        borderRadius: role === ChatRole.USER ? '16px' : 0,
        padding: role === ChatRole.USER ? theme.spacing(2, 3) : 0,
        background: role === ChatRole.USER ? theme.palette.ensightful.neutral[100] : 'transparent',
        color: theme.palette.ensightful.neutral[900],
    };
});

const AssistantMessageContent = styled('div')(() => {
    return {
        width: '100%',
    };
});

const MessageMetaRow = styled(Stack, {
    shouldForwardProp: (prop) => prop !== 'role',
})(({ role }) => {
    return {
        alignItems: 'center',
        justifyContent: role === ChatRole.USER ? 'flex-end' : 'flex-start',
    };
});

const MessageTimestamp = styled(Text, {
    shouldForwardProp: (prop) => prop !== 'role',
})(({ role }) => {
    return {
        textAlign: role === ChatRole.USER ? 'right' : 'left',
    };
});

const CopyButton = styled(IconButton)(() => {
    return {
        minWidth: 0,
    };
});

const LoadingMessageRow = styled(Stack)(() => {
    return {
        alignItems: 'flex-start',
    };
});

const LoadingMessageText = styled(Text)(({ theme }) => {
    return {
        background: `linear-gradient(90deg, 
            ${theme.palette.ensightful.neutral[800]} 0%, 
            ${theme.palette.ensightful.neutral[400]} 50%, 
            ${theme.palette.ensightful.neutral[800]} 100%
        )`,
        backgroundSize: '180px 100%',
        backgroundClip: 'text',
        WebkitBackgroundClip: 'text',
        color: 'transparent',
        WebkitTextFillColor: 'transparent',
        animation: 'chat-loading-gradient 2s linear infinite',

        '@keyframes chat-loading-gradient': {
            '0%': {
                backgroundPosition: '0px center',
            },
            '100%': {
                backgroundPosition: '180px center',
            },
        },
    };
});

export default ChatMessages;
